import { Box } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  FormikCheckboxField,
  FormikDropdownField,
} from '@components/FormikFields';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SelfReportedPill from '@components/SelfReportedPill';

import * as ROUTES from '@constants/routes';

import { useCuration } from '@hooks/useCuration';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useSession } from '@hooks/useSession';

import type { FhirResource } from '@models/fhir-resource';

import { ResourceSourceType } from 'generated/types';

const messages = defineMessages({
  selfReported: {
    id: 'GLOBAL.selfReported',
    defaultMessage: 'Self-Reported',
  },
  recordSource: {
    id: 'drilldownHeader.recordSource',
    defaultMessage: 'Record Source',
  },
});

interface CurationDrilldownSourceTypeProps {
  resource: FhirResource | null;
  selfReported: boolean;
}

export default function CurationDrilldownSourceType({
  selfReported,
  resource,
}: CurationDrilldownSourceTypeProps) {
  const formik = useFormikContext<Record<string, unknown>>();
  const { formatMessage } = useIntl();
  const { flags } = useFeatureFlags();

  const {
    state: { isCreating, isEditing },
  } = useCuration();
  const {
    state: { isClient },
  } = useSession();

  const selfReportedField =
    !isClient &&
    (isEditing || isCreating) &&
    !location.pathname.includes('insurance') ? (
      <FormikCheckboxField
        label={formatMessage(messages.selfReported)}
        name="selfReported"
        onChange={(value) => {
          // Changing self reported to true should make clinical status false
          if (location.pathname.includes(ROUTES.VITAL_SIGNS) && value) {
            formik.setFieldValue('clinicalStatus', false);
          }
        }}
      />
    ) : (
      <SelfReportedPill header value={selfReported} />
    );

  {
    /** TODO: Hide self-reported pill for vitall and practitioner roles
     * Need to check if record is self-reported upon creation and update and change source_type to self
     */
  }
  const sourceTypeField =
    flags.isRecordChangeRecordSourceEnabled && (isCreating || isEditing) ? (
      <Box sx={{ width: '25%' }}>
        <FormikDropdownField
          label={formatMessage(messages.recordSource)}
          labelColor="primary.contrastText"
          name="recordSource"
          onChange={(value) => {
            formik.setFieldValue('source_type', value);
            // Changing self reported to true should make clinical status false
            if (
              location.pathname.includes(ROUTES.VITAL_SIGNS) &&
              value === ResourceSourceType.self
            ) {
              formik.setFieldValue('clinicalStatus', false);
            }
          }}
          options={Object.values(ResourceSourceType).map((type) => ({
            k: type,
            v: `${formatMessage({
              id: `recordSourcePill.${type}`,
            })}`,
          }))}
          required
        />
      </Box>
    ) : (
      <RecordSourcePill style={{ mt: 1 }} type={resource?.recordSource} />
    );

  return (
    <>
      {selfReportedField}
      {sourceTypeField}
    </>
  );
}
